import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CircularProgress, InputAdornment } from "@mui/material";
import { useQueryClient } from "react-query";

import TextField from "../../../Components/Form Fields/text-field";
import PrimaryButton from "../../../Components/Buttons/primary-button";
import { durationOptions, frequencyOptions } from "../../../Pages/Rent Payment";

import renderRentPaymentDetails from "./rent-payment-details";
import renderStatusModal from "../status-modal";

import { useModalContext } from "../../../Contexts/ModalContext";

import { useStakeDebit } from "../../../CustomHooks/Queries";
import {
  useCreateRecurringRentPayment,
  useInitiateOnePayment,
  useUpdateRecurringRentPayment,
} from "../../../CustomHooks/Mutations";

import { getTransformedRentPaymentTransaction } from "../../../Utils/api-data-transforms";
import {
  capitaliseString,
  formatCurrency,
  getRecurringStartDate,
  getNumberWithOrdinal,
  getUserId,
  getRecurringEndDate,
  formatDate,
} from "../../../Utils/helpers";
import { APIQueryKeys, longMonthDateFormat, modalVariants } from "../../../Utils/constants";

import { CircleExclamationOutlined, CloseWithShadow } from "../../../Assets/Icons";

export default function renderConfirmPayment(modalContext, props) {
  const { updateModalComponent, pushModalStack, setModalVariant, setModalTitle, setModalClassName } = modalContext;

  updateModalComponent(() => <ConfirmPayment {...props} />);
  pushModalStack(() => renderConfirmPayment(modalContext, props));
  setModalVariant(modalVariants.flexDialog);
  setModalClassName("has-transfer-info full-modal-width hide-back-button");
  setModalTitle("Confirm your payment");
}

const ConfirmPayment = ({
  cashAmount,
  selectedPayee,
  isRecurringTransaction,
  editTransaction,
  recurringFieldValues,
  onConfirm,
}) => {
  const [notes, setNotes] = useState(editTransaction?.notes ?? "");

  const navigate = useNavigate();

  const modalContext = useModalContext();
  const { onModalClose, popModalStack } = modalContext;

  const queryClient = useQueryClient();

  const { data: stakeDebit } = useStakeDebit();

  const { recurringStartDate, recurringEndDate } = useMemo(() => {
    const startDate = getRecurringStartDate(recurringFieldValues?.frequency, recurringFieldValues?.recurringDay);
    const endDate = getRecurringEndDate({
      frequency: recurringFieldValues?.frequency,
      startDate,
      durationType: recurringFieldValues?.duration,
      noOfPayments: recurringFieldValues?.noOfPayments,
      specificDate: recurringFieldValues?.recurringEndDate,
    });

    return { recurringStartDate: startDate, recurringEndDate: endDate };
  }, [recurringFieldValues]);

  const onConfirmSuccess = ({ data }) => {
    queryClient.refetchQueries(isRecurringTransaction ? APIQueryKeys.recurringRentPayments : APIQueryKeys.rentPayments);
    onConfirm();
    renderRentPaymentDetails(modalContext, {
      isAPIResponseStatus: true,
      isRecurringTransaction,
      isEditing: !!editTransaction,
      paymentDetails: getTransformedRentPaymentTransaction([data?.data])[0],
    });
  };

  const onConfirmError = () =>
    renderStatusModal(modalContext, {
      StatusIcon: CloseWithShadow,
      title: isRecurringTransaction ? "Recurring Payment Failed" : "Payment Failed",
      description: `We encountered an issue while ${
        isRecurringTransaction ? (!!editTransaction ? "updating" : "setting up") : "processing"
      } your${isRecurringTransaction ? " recurring" : ""} payment. Please try again.`,
      CTAButtonText: "Retry",
      onCTAButtonClick: () => popModalStack(0),
      secondaryCTAButtonText: "Cancel",
      onSecondaryCTAButtonClick: onModalClose,
    });

  const { isLoading: isInitiatingOneTimePayment, mutate: InitiateOnePayment } = useInitiateOnePayment({
    onSuccess: onConfirmSuccess,
    onError: onConfirmError,
  });

  const { isLoading: isCreatingRecurringRentPayment, mutate: CreateRecurringRentPayment } =
    useCreateRecurringRentPayment({
      onSuccess: onConfirmSuccess,
      onError: onConfirmError,
    });

  const { isLoading: isUpdatingRecurringRentPayment, mutate: UpdateRecurringRentPayment } =
    useUpdateRecurringRentPayment({
      onSuccess: onConfirmSuccess,
      onError: onConfirmError,
    });

  const hasInsufficientBalance = cashAmount > stakeDebit?.data?.data?.available / 100;

  const handleAuthorize = () => {
    if (hasInsufficientBalance) {
      navigate("/banking/add-cash");
      onModalClose();
    } else {
      const paymentProps = {
        amount: cashAmount,
        destination_counter_party_id: selectedPayee?.counter_party_id,
        mobile_user_id: getUserId(),
        comments: notes,
      };

      if (isRecurringTransaction) {
        const recurringProps = {
          duration: recurringFieldValues?.duration,
          end_date: recurringFieldValues?.recurringEndDate,
          no_of_payments: recurringFieldValues?.noOfPayments,
          frequency_type: recurringFieldValues?.frequency,
          frequency_day: recurringFieldValues?.recurringDay,
        };
        if (!editTransaction) {
          CreateRecurringRentPayment({
            ...paymentProps,
            ...recurringProps,
          });
        } else {
          UpdateRecurringRentPayment({
            ...paymentProps,
            ...recurringProps,
            recurringId: editTransaction?.id,
          });
        }
      } else {
        InitiateOnePayment(paymentProps);
      }
    }
  };

  const isLoading = isInitiatingOneTimePayment || isCreatingRecurringRentPayment || isUpdatingRecurringRentPayment;

  return (
    <>
      <div className="modal-dialog-content">
        <div className="payee-card">
          <div className="account-badge">{selectedPayee?.nameAcronym}</div>
          <div className="info-container">
            <p className="title">{selectedPayee?.account}</p>
            <p className="description">{selectedPayee?.name}</p>
          </div>
        </div>
        <div className="payment-details mb-4">
          <div className="transfer-detail">
            <span className="detail-label">Amount</span>
            <span className="detail-value">{formatCurrency(cashAmount)}</span>
          </div>
          <div className="transfer-detail">
            <span className="detail-label">Fee</span>
            <span className="detail-value">Free</span>
          </div>
          {isRecurringTransaction && (
            <>
              <div className="transfer-detail">
                <span className="detail-label">Frequency</span>
                <span className="detail-value">
                  {capitaliseString(recurringFieldValues?.frequency)} on{" "}
                  {recurringFieldValues?.frequency === frequencyOptions[2].value
                    ? `the ${getNumberWithOrdinal(recurringFieldValues?.recurringDay)}`
                    : capitaliseString(recurringFieldValues?.recurringDay)}
                </span>
              </div>
              {recurringFieldValues.duration === durationOptions[1].value && (
                <div className="transfer-detail">
                  <span className="detail-label">Number of Payments</span>
                  <span className="detail-value">{recurringFieldValues?.noOfPayments}</span>
                </div>
              )}
              <div className="transfer-detail">
                <span className="detail-label">Start Date</span>
                <span className="detail-value">{formatDate(recurringStartDate, longMonthDateFormat)}</span>
              </div>
              {recurringFieldValues.duration === durationOptions[2].value && (
                <div className="transfer-detail">
                  <span className="detail-label">Last Payment Date</span>
                  <span className="detail-value">{formatDate(recurringEndDate, longMonthDateFormat)}</span>
                </div>
              )}
              <div className="transfer-detail">
                <span className="detail-label">End Date</span>
                <span className="detail-value">
                  {recurringFieldValues.duration === durationOptions[0].value
                    ? durationOptions[0].label
                    : formatDate(
                        recurringFieldValues.duration === durationOptions[1].value
                          ? recurringEndDate
                          : recurringFieldValues?.recurringEndDate,
                        longMonthDateFormat
                      )}
                </span>
              </div>
            </>
          )}
          <div className="transfer-detail">
            <span className="detail-label">From</span>
            <span className="detail-value">Stake Checking · {stakeDebit?.data?.data?.account_number?.slice(-4)}</span>
          </div>
        </div>
        <TextField
          className="filled mb-4"
          label="Note (optional)"
          placeholder="Add note here"
          value={notes}
          onChange={(e) => setNotes((_) => e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <span className="text-xs-regular color--mist">{notes.length}/20</span>
              </InputAdornment>
            ),
          }}
          inputProps={{ maxLength: 20 }}
        />
        {hasInsufficientBalance && (
          <div className="info-alert error mb-4">
            <CircleExclamationOutlined />
            Insufficient balance to make this payment. Please add cash to complete your payment.
          </div>
        )}
        <span className="disclaimer">
          *
          {isRecurringTransaction
            ? "Transfers take one business day to process. Transfers set up on a weekend, or holiday, will occur on the next business day."
            : "2-3 business days. Transactions made after 3:00 PM ET or on a weekend or holiday will be processed the next business day."}
          *
        </span>
      </div>
      <div className="action-container split-equally">
        <PrimaryButton onClick={handleAuthorize} disabled={isLoading}>
          {hasInsufficientBalance ? "Add Cash" : "Authorize"} {isLoading && <CircularProgress />}
        </PrimaryButton>
        <PrimaryButton className="secondary-colors" onClick={() => popModalStack(1)}>
          Cancel
        </PrimaryButton>
      </div>
    </>
  );
};
